import * as moment from "moment";
import {JobEventApiDto} from "../../../api/model/rest";
import AuthenticatedEntity from "../../user/AuthenticatedEntity";
import JobFilesAttachedEvent from "./JobFilesAttachedEvent";
import JobBDPChangedEvent from "./JobBDPChangedEvent";
import JobAgentChangedEvent from "./JobAgentChangedEvent";
import JobStatusEvent from "./JobStatusEvent";
import JobWatcherEvent from "./JobWatcherEvent";
import JobSmsSentEvent from "./JobSmsSentEvent";
import JobExternalCallRegisteredEvent from "./JobExternalCallRegisteredEvent";
import {Icon} from "../../../hiberUiKit/src/atoms/Icon/Icon";
import {IconColor} from "../../../hiberUiKit/src/molecules/StreamItem/StreamItemIcon";
import Attachment from "../../attachment/Attachment";
import ActivityEvent from "../../event/ActivityEvent";
import JobInvoicePathChangedEvent from "./JobInvoicePathChangedEvent";
import JobMarkedAsPaidEvent from "./JobMarkedAsPaidEvent";
import JobUnmarkedAsPaidEvent from "./JobUnMarkedAsPaidEvent";

/**
 * Job event model.
 */
export default class JobEvent extends ActivityEvent {
	/**
	 * @param {string} id identifier of event.
	 * @param {string} jobId job id.
	 * @param {JobEventTypeEnum} type type of the event.
	 * @param {AuthenticatedEntity} entity entity which creates the event.
	 * @param {moment.Moment} creationDate creation date of event.
	 * @param {string} referenceNumber job reference number.
	 */
	constructor(public readonly id: string,
		public readonly jobId: string,
		public readonly type: JobEventTypeEnum,
		public readonly entity: AuthenticatedEntity,
		public readonly creationDate: moment.Moment,
		public readonly referenceNumber: string) {
		super();
	}

	getCssClass() {
		return '';
	}

	getIcon() {
		switch (this.type) {
			case JobEventTypeEnum.MarkedAsPaid:
				return Icon.Invoice;
			default:
				return Icon.New;
		}
	}

	getIconColor(): IconColor {
		return IconColor.Orange;
	}

	getAttachments(): Attachment[] {
		return [];
	}

	getActivityAdditionalMessages(): string[] {
		return [];
	}

	getActivityType(): string {
		return `JOB`;
	}

	getActivityMessage(): string {
		let actionPerformed: string;
		switch (this.type) {
			case JobEventTypeEnum.Created:
				actionPerformed = "created";
				break;
			case JobEventTypeEnum.CreatedFromQuote:
				actionPerformed = "created from quote";
				break;
			case JobEventTypeEnum.Updated:
				actionPerformed = "updated";
				break;
			case JobEventTypeEnum.MarkedAsPaid:
				actionPerformed = "marked as paid";
				break;
			default:
				actionPerformed = "created";
		}
		return `Job ${actionPerformed}`;
	}

	getCreationDate(): moment.Moment {
		return this.creationDate;
	}

	getId() {
		return this.id;
	}

	/**
	 * Get user display name.
	 */
	getUserDisplayName(): string {
		return this.entity.getDisplayName();
	}

	public static fromJson(json: JobEventApiDto): JobEvent {
		let type: JobEventTypeEnum;
		switch (json.type) {
			case JobEventTypeEnum.Created:
				type = JobEventTypeEnum.Created;
				break;
			case JobEventTypeEnum.Updated:
				type = JobEventTypeEnum.Updated;
				break;
			case JobEventTypeEnum.CreatedFromQuote:
				type = JobEventTypeEnum.CreatedFromQuote;
				break;
			case JobEventTypeEnum.MarkedAsPaid:
				type = JobEventTypeEnum.MarkedAsPaid;
				break;
			default:
				type = JobEventTypeEnum.Created;
		}
		const entity = AuthenticatedEntity.fromJson(json.entity);
		return new JobEvent(json.id, json.jobId.toString(), type, entity, moment(json.createdAt), json.referenceNumber);
	}
}

export enum JobEventTypeEnum {
	StatusChanged = "STATUS_CHANGED",
	Created = "CREATED",
	Updated = "UPDATED",
	CreatedFromQuote = "CREATED_FROM_QUOTE",
	JobFilesAttached = "JOB_FILES_ATTACHED",
	JobFilesDeleted = "JOB_FILES_DELETED",
	AppointmentCreated = "APPOINTMENT_CREATED",
	AppointmentUpdated = "APPOINTMENT_UPDATED",
	AppointmentStatusChanged = "APPOINTMENT_STATUS_CHANGED",
	BdpAssigneeChanged = "JOB_BUSINESS_DEVELOPMENT_PARTNER_ASSIGNED",
	AgentAssigneeChanged = "JOB_AGENT_ASSIGNED",
	WatchersChanged = "WATCHERS_CHANGED",
	JobSmsSent = "JOB_SMS_SENT",
	Note = "NOTE",//dummy type used for filtering
	NoteReminder = "REMINDER",//dummy type used for filtering
	QuoteAccepted = "QUOTE_ACCEPTED",
	SurveyCreated = "SURVEY_CREATED",
	JobEmailSent = "JOB_EMAIL_SENT",
	NoteCreated = "NOTE_CREATED", // dummy type used for job last action
	ExternalCallRegistered = "EXTERNAL_CALL_REGISTERED",
	SurveyFilesAttached = "SURVEY_FILES_ATTACHED",
	InvoicePathChanged = "INVOICE_PATH_CHANGED",
	MarkedAsPaid = "JOB_MARKED_AS_PAID",
	UnMarkedAsPaid = "JOB_UNMARKED_AS_PAID"
}

export enum JobSmsEventSubtypeEnum {
	JobAppointmentConfirmationSms = "JOB_APPOINTMENT_CONFIRMATION_SMS",
	JobFreeTextContactSms = "JOB_FREE_TEXT_CONTACT_SMS",
	JobFreeTextCreatorSms = "JOB_FREE_TEXT_CREATOR_SMS"
}

export enum JobEmailEventSubtypeEnum {
	JobFreeTextContactEmail = "JOB_FREE_TEXT_CONTACT_EMAIL"
}

export type JobEventsType =
	JobEvent
	| JobFilesAttachedEvent
	| JobBDPChangedEvent
	| JobAgentChangedEvent
	| JobStatusEvent
	| JobWatcherEvent
	| JobSmsSentEvent
	| JobExternalCallRegisteredEvent
	| JobInvoicePathChangedEvent
	| JobMarkedAsPaidEvent
	| JobUnmarkedAsPaidEvent;
